import React, { useState, useEffect, useMemo } from 'react';
import {
  useForm,
  FormProvider,
  Controller,
  type SubmitHandler,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  SidePanel,
  Button,
  Icon,
  InputWithLabel,
  DocumentUploadSection,
  Dropdown,
  SearchInputSelection,
  TextareaWithLabel,
  useRefreshPageStore,
} from '@tasama/ui';
import { useTranslation } from '@tasama/i18n';
import { useFileUploadProgress } from '@tasama/utils';
import {
  useCreateTicket,
  useFetchDropdownOptions,
  useFetchSupportIssues,
} from '../../features/support-ticketing/hooks';
import { resolveApiErrorMessage } from '@/shared/utils';
import { handleApiError, type ApiError } from '@tasama/api-client-query';
import { TopicCategory } from '../../features/support-ticketing/types';
import {
  useSupportTicketsStore,
  useNewTicketDrawerStore,
  useNewTicketUploadStore,
  type NewTicketUploadedFile,
} from '../../features/support-ticketing/store';
import {
  validateNewTicketUploadForm,
  newTicketFormDefaultValues,
  getNewTicketFormSchema,
  type NewTicketFormValues,
} from '../../features/support-ticketing/schemas';
import { DUPLICATE_DYNAMIC_FIELD_NAMES } from '../../features/support-ticketing/constants';
import useAuth from '@/hooks/use-auth';
import { useInfiniteMyServicesQuery } from '../../features/my-services/hooks/useInfiniteMyServicesQuery';
import { useServiceTitlesListQuery } from '@/hooks/useServiceTitlesListQuery';
import { useCatalogItemSchemaQuery } from '../../features/my-services/hooks/use-catalog-item-schema-query.hook';
import { DynamicFormRenderer } from '../../features/my-services/components/dynamic-form/DynamicFormRenderer';
import { flattenFields } from '../../features/my-services/schemas/dynamic-activity-request.schema';
import { trackEvent } from '@tasama/utils';
import type { FormSectionSchema } from '@tasama/types';

// Static form field names (see NewTicketFormValues) — a backend catalog-item schema can
// legitimately name one of its own dynamic fields "description" (e.g. ServiceNow's own
// Description field). Guards the dynamic-field cleanup effect below from ever clearing
// these when a dynamic field name collides with one.
const STATIC_FORM_FIELD_NAMES = new Set<string>([
  'topic',
  'subject',
  'description',
  'functionL1',
  'typeL2',
  'serviceL3',
]);

// TDF-1432/1434: DUPLICATE_DYNAMIC_FIELD_NAMES (short_description/description) would
// otherwise render a second time alongside the drawer's own static Subject/Description
// inputs — see its definition for why the backend makes those two safe to drop.
//
// A file-type field is dropped here too, but only when it isn't mandatory: the backend
// attaches whatever the client uploads via the drawer's own shared DocumentUploadSection to
// the created record regardless of whether a file-type variable exists, so a non-mandatory
// one would just duplicate that upload. A *mandatory* file-type field is not safe to drop
// this way: DynamicFormRenderer/onSubmit below have no upload wiring for a named dynamic
// file field (only the shared DocumentUploadSection), so it can't be rendered here yet
// either. Silently excluding it would let the ticket submit as "complete" while a genuine
// ServiceNow-required variable is left unset — so hasUnsupportedMandatoryFileField (below)
// blocks SUBMIT for that case instead of pretending the requirement doesn't exist.
function excludeDuplicateFields(
  sections: FormSectionSchema[]
): FormSectionSchema[] {
  return sections
    .map((section) =>
      section.multiRow
        ? section
        : {
            ...section,
            fields: (section.fields ?? []).filter(
              (field) =>
                field.type !== 'file' &&
                !DUPLICATE_DYNAMIC_FIELD_NAMES.has(field.name)
            ),
          }
    )
    .filter((section) => section.multiRow || (section.fields?.length ?? 0) > 0);
}

// A mandatory file-type field this drawer has no upload wiring for (see comment above) — its
// presence must disable SUBMIT rather than silently vanish from what's validated.
function hasUnsupportedMandatoryFileField(
  sections: FormSectionSchema[]
): boolean {
  return sections.some(
    (section) =>
      !section.multiRow &&
      (section.fields ?? []).some(
        (field) => field.type === 'file' && field.mandatory === true
      )
  );
}

export interface NewTicketDrawerProps {
  testID?: string;
  requestId?: string;
  serviceName?: string;
}

export const NewTicketDrawer: React.FC<NewTicketDrawerProps> = (props) => {
  const { isAuthenticated } = useAuth();
  const close = useNewTicketDrawerStore((s) => s.close);
  const isOpen = useNewTicketDrawerStore((s) => s.isOpen);

  useEffect(() => {
    if (!isAuthenticated && isOpen) {
      close();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAuthenticated) return null;
  return <NewTicketDrawerContent {...props} />;
};

const NewTicketDrawerContent: React.FC<NewTicketDrawerProps> = ({
  testID = 'new-ticket-drawer',
  requestId,
  serviceName,
}) => {
  const { t } = useTranslation('portal');

  // Global store for drawer state — serviceId/L1/L2 come from store (set by callers via open())
  const {
    isOpen,
    close,
    triggerSuccess,
    serviceId: storeServiceId,
    serviceL1: storeServiceL1,
    serviceL2: storeServiceL2,
    serviceL3: storeServiceL3,
    requestId: storeRequestId,
    serviceName: storeServiceName,
    srAttemptId: storeSrAttemptId,
    parentSrId: storeParentSrId,
    serviceStatus: storeServiceStatus,
    bundleAttemptId: storeBundleAttemptId,
  } = useNewTicketDrawerStore();

  const effectiveRequestId = requestId ?? storeRequestId;
  const effectiveServiceName = serviceName ?? storeServiceName;
  const isServiceRequestContextMode = Boolean(effectiveRequestId?.trim());

  // DOM gating with slide-in/out transition support
  const [isMounted, setIsMounted] = useState(false);
  const [visuallyOpen, setVisuallyOpen] = useState(false);

  // Effect 1: controls isMounted for slide-in/out transition support
  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      return;
    } else {
      setVisuallyOpen(false);
      // Wait for SidePanel's duration-300 to finish before unmounting
      const timer = setTimeout(() => setIsMounted(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Effect 2: triggers slide-in only after React has committed isMounted=true to the DOM
  useEffect(() => {
    if (!isMounted || !isOpen) return;
    const rafId = requestAnimationFrame(() => setVisuallyOpen(true));
    return () => cancelAnimationFrame(rafId);
  }, [isMounted, isOpen]);

  // Use React Query mutation hooks for creating tickets
  const { mutateAsync: createTicket, isPending: isCreatingTicket } =
    useCreateTicket();

  // Fetch dropdown options only when drawer is open
  const { data: dropdownOptions } = useFetchDropdownOptions(isOpen);

  // TDF-1388/TDF-1390: Topic → Sub-Topic → Issue catalog for the two new topics
  const { data: supportIssuesCatalog } = useFetchSupportIssues(isOpen);

  // The dynamic-form validation schema (below, near dynamicFormSchema) depends on which
  // issue/topic is selected, which in turn is only known via this form's own `watch('topic')`
  // — so the form has to exist before that schema can be computed. Resolved via a ref the
  // real resolver reads at validate-time, not render-time, instead of feeding useForm() a
  // schema built from a value only useForm() itself can produce.
  const dynamicFormSchemaRef = React.useRef<ReturnType<
    typeof getNewTicketFormSchema
  > | null>(null);

  const formMethods = useForm<NewTicketFormValues>({
    resolver: (values, context, options) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      zodResolver(dynamicFormSchemaRef.current as any)(
        values,
        context,
        options
      ),
    // TDF-1432: none of this drawer's Dropdown/SearchInputSelection controls wire an
    // onBlur, so 'onTouched' (validate on first blur) would never fire for them — 'onChange'
    // re-validates the whole schema on every change instead, which is what keeps
    // formState.isValid (isFormValid, below) live as the user fills the form.
    mode: 'onChange',
    defaultValues: newTicketFormDefaultValues,
  });
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isValid },
  } = formMethods;

  const { topic, functionL1, typeL2 } = watch();

  // TDF-1388/TDF-1390 AC2: IT Services carries a single implicit issue the client never
  // names — but only once IT Services is the *selected* topic. Previously unguarded, so
  // this always resolved truthy as soon as the catalog loaded and won every `??` below,
  // silently rendering IT Services' fields (User Impact/User Urgency) under every topic,
  // including the 4 existing ones which TDF-1390 says must render subject/description/
  // attachments only.
  const itServicesIssue = useMemo(
    () =>
      topic === TopicCategory.IT_SERVICES
        ? supportIssuesCatalog?.topics.find(
            (t) => t.topic === TopicCategory.IT_SERVICES
          )?.subTopics[0]?.issues[0]
        : undefined,
    [supportIssuesCatalog, topic]
  );

  const oracleErpTopic = useMemo(
    () =>
      supportIssuesCatalog?.topics.find(
        (t) => t.topic === TopicCategory.ORACLE_ERP_ISSUES
      ),
    [supportIssuesCatalog]
  );

  const subTopicOptions = useMemo(
    () =>
      (oracleErpTopic?.subTopics ?? [])
        .filter((st) => st.subTopic)
        .map((st) => ({
          value: st.subTopic as string,
          label: st.subTopic as string,
        })),
    [oracleErpTopic]
  );

  // TDF-1388: subTopic/issueKey are deliberately plain component state, not RHF fields —
  // same reason srAttemptId (below) isn't one either: the Issue selection drives the
  // TDF-1390 dynamic schema fetch, and feeding that back into this form's own zodResolver
  // would be circular. isFormValid (below) enforces AC6 manually, same as srAttemptId today.
  const [selectedSubTopic, setSelectedSubTopic] = useState<
    string | undefined
  >();
  const [selectedIssueKey, setSelectedIssueKey] = useState<
    string | undefined
  >();

  const issueOptions = useMemo(
    () =>
      (
        oracleErpTopic?.subTopics.find((st) => st.subTopic === selectedSubTopic)
          ?.issues ?? []
      ).map((issue) => ({ value: issue.issueKey, label: issue.name })),
    [oracleErpTopic, selectedSubTopic]
  );

  // Defense in depth alongside the `topic` gate above: the Topic dropdown's onChange
  // already clears selectedSubTopic/selectedIssueKey on every topic change (AC5), but this
  // keeps the resolution correct even if some other path sets `topic` without going through
  // that handler.
  const selectedOracleErpIssue = useMemo(
    () =>
      topic === TopicCategory.ORACLE_ERP_ISSUES
        ? oracleErpTopic?.subTopics
            .flatMap((st) => st.issues)
            .find((issue) => issue.issueKey === selectedIssueKey)
        : undefined,
    [topic, oracleErpTopic, selectedIssueKey]
  );

  const { getPrefilledTicketData, clearPrefilledTicketData } =
    useSupportTicketsStore();

  // File upload store — read from state.uploadedFiles (not formData.uploadedFiles) to preserve File objects for submission
  const uploadedFiles = useNewTicketUploadStore((s) => s.uploadedFiles);
  const uploadedFilesRef = React.useRef(uploadedFiles);
  uploadedFilesRef.current = uploadedFiles;
  const addFiles = useNewTicketUploadStore((s) => s.addFiles);
  const updateFile = useNewTicketUploadStore((s) => s.updateFile);
  const removeFile = useNewTicketUploadStore((s) => s.removeFile);
  const validateFiles = useNewTicketUploadStore((s) => s.validateFiles);
  const resetUploadState = useNewTicketUploadStore((s) => s.resetUploadState);

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [fileError, setFileError] = useState<string | undefined>();
  const [isFileRemoved, setIsFileRemoved] = useState(false);
  const [selectedSrAttemptId, setSelectedSrAttemptId] = useState<
    string | undefined
  >();

  // Active services for the "Active Services" topic selector
  const {
    data: activeServicesData,
    fetchNextPage: fetchNextActiveServicesPage,
    hasNextPage: hasNextActiveServicesPage,
    isFetchingNextPage: isFetchingNextActiveServicesPage,
    isError: isActiveServicesError,
  } = useInfiniteMyServicesQuery({
    status: 'ACTIVE',
    size: 50,
    enabled: isOpen,
  });

  // Requested services for the "Requested Services" topic selector
  const {
    data: requestedServicesData,
    fetchNextPage: fetchNextRequestedServicesPage,
    hasNextPage: hasNextRequestedServicesPage,
    isFetchingNextPage: isFetchingNextRequestedServicesPage,
    isError: isRequestedServicesError,
  } = useInfiniteMyServicesQuery({
    status: 'REQUESTED',
    size: 50,
    enabled: isOpen,
  });

  useEffect(() => {
    if (isActiveServicesError || isRequestedServicesError) {
      useRefreshPageStore.getState().open();
    }
  }, [isActiveServicesError, isRequestedServicesError]);

  const { serviceTitles } = useServiceTitlesListQuery();
  const activeServiceOptions = useMemo(() => {
    const services = activeServicesData?.pages.flatMap((p) => p.services) ?? [];
    return services.map((svc) => ({
      value: svc.srAttemptId,
      label: `${svc.requestId} – ${(svc.serviceSysId ? serviceTitles?.[svc.serviceSysId] : null) ?? svc.serviceName}`,
    }));
  }, [activeServicesData, serviceTitles]);
  // TDF-1342 (added 2026-09-09): the Requested tab's own bundle cards
  // (bundleAttemptId present, bundleItemType null — the pre-split
  // discrimination rule, same as MyServicesPage's isBundleCard) are also a
  // valid "Requested Service" ticket subject — BE resolves bundle_attempt_id
  // the same way it resolves sr_attempt_id. Tracked in requestedBundleIds so
  // onSubmit knows which form field to send the selected id under.
  const requestedServiceOptions = useMemo(() => {
    const services =
      requestedServicesData?.pages.flatMap((p) => p.services) ?? [];
    return services.map((svc) => {
      const isBundle = Boolean(svc.bundleAttemptId) && !svc.bundleItemType;
      if (isBundle) {
        return {
          value: svc.bundleAttemptId as string,
          label: `${svc.bundleRitmNumber} – ${(svc.bundleSysId ? serviceTitles?.[svc.bundleSysId] : null) ?? svc.bundleName}`,
        };
      }
      return {
        value: svc.srAttemptId,
        label: `${svc.requestId} – ${(svc.serviceSysId ? serviceTitles?.[svc.serviceSysId] : null) ?? svc.serviceName}`,
      };
    });
  }, [requestedServicesData, serviceTitles]);
  const requestedBundleIds = useMemo(() => {
    const services =
      requestedServicesData?.pages.flatMap((p) => p.services) ?? [];
    return new Set(
      services
        .filter((svc) => Boolean(svc.bundleAttemptId) && !svc.bundleItemType)
        .map((svc) => svc.bundleAttemptId as string)
    );
  }, [requestedServicesData]);
  const { startProgress, cancelProgress } = useFileUploadProgress();

  // Loading state from ticket creation mutation
  const isSubmitting = isCreatingTicket;

  // TDF-1390: producerSysId is what feeds the dynamic form schema lookup. IT Services
  // resolves it as soon as the topic is picked (single item, no further selection);
  // Oracle ERP Issues resolves it once both Sub-Topic and Issue are chosen.
  const supportIssueProducerSysId =
    itServicesIssue?.producerSysId ?? selectedOracleErpIssue?.producerSysId;

  const {
    schema: catalogItemSchema,
    loading: schemaLoading,
    error: schemaError,
  } = useCatalogItemSchemaQuery(supportIssueProducerSysId ?? '');
  const dynamicSections = excludeDuplicateFields(
    catalogItemSchema?.sections ?? []
  );
  const dynamicRules = catalogItemSchema?.staticRules ?? [];

  // Schema recreated each render (dynamicSections/dynamicRules change as the selected
  // issue changes) so the resolver stays current — same pattern as
  // ActivityRequestFormContainer. Written into the ref the form's own resolver (above)
  // reads at validate-time, since useForm() was created before this could be computed.
  const dynamicFormSchema = getNewTicketFormSchema(
    dynamicSections,
    dynamicRules,
    t
  );
  dynamicFormSchemaRef.current = dynamicFormSchema;

  // Gating on `!schemaLoading` alone treats a FAILED schema fetch as ready: dynamicSections
  // defaults to [] (line 365), so the zod schema has no dynamic-field requirements and isValid
  // can be true for a form that never rendered/validated that issue's mandatory fields — same
  // gap ActivityRequestFormContainer's schemaReady closes. The loading path was always
  // correct (a slow fetch keeps SUBMIT disabled via isValid depending on unset defaults), so
  // the guard is on the error path only.
  // A mandatory file field is excluded from dynamicSections above (excludeDuplicateFields —
  // no upload wiring exists for it yet), so it must block SUBMIT explicitly rather than
  // silently vanish from what isValid covers.
  const schemaReady =
    !schemaLoading &&
    !schemaError &&
    !hasUnsupportedMandatoryFileField(catalogItemSchema?.sections ?? []);

  // TDF-1432: subject/description-required, the Service Specific hierarchy, and every
  // dynamic issue field's own mandatory-ness are already enforced by the zod schema
  // (newTicketFormSchema / getNewTicketFormSchema) — isValid covers all of that. Only
  // selectedSrAttemptId/selectedSubTopic/selectedIssueKey are genuinely plain component
  // state outside the form (see their declarations above) and need a manual check here,
  // same as before.
  const isFormValid = (() => {
    if (
      topic === TopicCategory.ACTIVE_SERVICES ||
      topic === TopicCategory.REQUESTED_SERVICES
    )
      return isValid && schemaReady && !!selectedSrAttemptId;
    if (topic === TopicCategory.ORACLE_ERP_ISSUES)
      return isValid && schemaReady && !!selectedSubTopic && !!selectedIssueKey;
    return isValid && schemaReady;
  })();

  // TDF-1390 AC3/TDF-1434: Subject arrives pre-filled with the issue name and stays
  // editable — and re-syncs to the new issue's name whenever the selected issue changes,
  // so it never goes stale (TDF-1434). This overwrites whatever was in the field,
  // including a client's manual edit to the previous issue's prefill.
  useEffect(() => {
    if (topic !== TopicCategory.IT_SERVICES || !itServicesIssue) return;
    setValue('subject', itServicesIssue.name, { shouldValidate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic, itServicesIssue]);

  useEffect(() => {
    if (!selectedOracleErpIssue) return;
    setValue('subject', selectedOracleErpIssue.name, {
      shouldValidate: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOracleErpIssue]);

  // TDF-1390 AC7: changing the issue replaces its fields with the new item's — clear
  // whatever was registered under the previous issue's dynamic field names before the
  // new schema's fields render. Subject/description/attachments are untouched (not
  // part of dynamicSections).
  const prevDynamicFieldNamesRef = React.useRef<string[]>([]);
  useEffect(() => {
    prevDynamicFieldNamesRef.current.forEach((name) => {
      // A catalog item's own schema can name a dynamic field "subject"/"description"
      // (e.g. ServiceNow's own Description field) — never unregister the drawer's own
      // static fields just because a dynamic field name collided with one.
      if (STATIC_FORM_FIELD_NAMES.has(name)) return;
      setValue(name as never, undefined as never, { shouldValidate: false });
    });
    prevDynamicFieldNamesRef.current = flattenFields(dynamicSections).map(
      (f) => f.name
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supportIssueProducerSysId]);

  // TDF-1390 AC4: item's own placeholder, falling back to the standard copy.
  const descriptionPlaceholder =
    (topic === TopicCategory.IT_SERVICES
      ? itServicesIssue?.descriptionPlaceholder
      : selectedOracleErpIssue?.descriptionPlaceholder) ||
    t('supportTickets.drawer.describePlaceholder');

  // Extract hierarchical dropdown options with cascading logic
  const functionOptions = dropdownOptions || [];

  // Filter service types based on selected function
  const typeOptions = functionL1
    ? functionOptions.find((fn) => fn.value === functionL1)?.serviceTypes || []
    : [];

  // Filter offerings based on selected function and type
  const serviceOptions =
    functionL1 && typeL2
      ? functionOptions
          .find((fn) => fn.value === functionL1)
          ?.serviceTypes.find((type) => type.value === typeL2)?.offerings || []
      : [];

  // Reset upload state each time the drawer opens so stale files from a
  // previous session never block the upload zone (showUpload / maxFiles logic).
  useEffect(() => {
    if (isOpen) {
      resetUploadState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Use effect to set prefilled data when drawer opens
  useEffect(() => {
    if (isOpen) {
      const prefilledData = getPrefilledTicketData();
      reset((current) => ({
        ...current,
        subject: prefilledData.subject ?? '',
        description: prefilledData.description ?? '',
      }));
    }
  }, [isOpen, getPrefilledTicketData, storeServiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Function to populate service specific fields using store data
  const populateServiceSpecificFields = () => {
    if (!dropdownOptions) return;

    const targetOfferingInfo = storeServiceL3 || storeServiceId;
    let isOfferingFoundInTree = false;

    if (targetOfferingInfo && targetOfferingInfo.trim() !== '') {
      for (const serviceFunction of dropdownOptions) {
        if (!serviceFunction.serviceTypes) continue;

        for (const serviceType of serviceFunction.serviceTypes) {
          if (!serviceType.offerings) continue;

          const matchingOffering = serviceType.offerings.find(
            (o) =>
              o.value === targetOfferingInfo || o.sysId === targetOfferingInfo
          );

          if (matchingOffering) {
            setValue('functionL1', serviceFunction.value, {
              shouldValidate: true,
            });
            setValue('typeL2', serviceType.value, { shouldValidate: true });
            setValue('serviceL3', matchingOffering.value, {
              shouldValidate: true,
            });
            isOfferingFoundInTree = true;
            break;
          }
        }
        if (isOfferingFoundInTree) break;
      }
    }

    if (!isOfferingFoundInTree) {
      if (storeServiceL1 && storeServiceL1.trim() !== '') {
        const matchingFunction = dropdownOptions.find(
          (fn) => fn.value === storeServiceL1 || fn.sysId === storeServiceL1
        );

        if (matchingFunction) {
          setValue('functionL1', matchingFunction.value, {
            shouldValidate: true,
          });

          if (storeServiceL2 && storeServiceL2.trim() !== '') {
            const matchingType = matchingFunction.serviceTypes?.find(
              (type) =>
                type.value === storeServiceL2 || type.sysId === storeServiceL2
            );

            if (matchingType) {
              setValue('typeL2', matchingType.value, { shouldValidate: true });
            }
          }
        }
      }
    }
  };

  // Use effect to handle service/bundle context pre-selection
  useEffect(() => {
    if (!isOpen || !dropdownOptions) return;

    const setTopic = (topic: TopicCategory) =>
      setValue('topic', topic, { shouldValidate: true });

    // TDF-1342 (added 2026-09-09): a bundle context has no serviceId/serviceL3
    // (a bundle is several L3s by definition, per the contract) and its only
    // valid topic is "Requested Service" — an independent branch from the SR
    // context below, which requires hasServiceDetails.
    if (storeBundleAttemptId?.trim()) {
      setTopic(TopicCategory.REQUESTED_SERVICES);
      setSelectedSrAttemptId(storeBundleAttemptId);
      return;
    }

    const hasAttemptId = !!storeSrAttemptId?.trim();
    const hasServiceDetails =
      !!storeServiceId?.trim() || !!storeServiceL3?.trim();

    const getTopicForStatus = (status?: string): TopicCategory => {
      switch (status) {
        case 'REQUESTED':
          return TopicCategory.REQUESTED_SERVICES;
        case 'ACTIVE':
          return TopicCategory.ACTIVE_SERVICES;
        default:
          return TopicCategory.SERVICE_SPECIFIC;
      }
    };

    if (!hasServiceDetails) {
      return;
    }
    if (!hasAttemptId) {
      setTopic(TopicCategory.SERVICE_SPECIFIC);
      populateServiceSpecificFields();
      return;
    }

    const topic = getTopicForStatus(storeServiceStatus);
    setTopic(topic);

    if (topic === TopicCategory.SERVICE_SPECIFIC) {
      populateServiceSpecificFields();
    } else {
      setSelectedSrAttemptId(storeSrAttemptId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    dropdownOptions,
    storeServiceId,
    storeServiceL1,
    storeServiceL2,
    storeServiceL3,
    storeSrAttemptId,
    storeServiceStatus,
    storeBundleAttemptId,
  ]);

  const onSubmit: SubmitHandler<NewTicketFormValues> = async (data) => {
    try {
      const formData = new FormData();

      formData.append('topic', data.topic);
      formData.append('subject', data.subject.trim());
      formData.append('description', data.description.trim());

      if (data.topic === TopicCategory.SERVICE_SPECIFIC) {
        const functionL1Label =
          functionOptions.find((fn) => fn.value === data.functionL1)?.label ??
          data.functionL1;
        const typeL2Label =
          typeOptions.find((t) => t.value === data.typeL2)?.label ??
          data.typeL2;
        const serviceL3Label =
          serviceOptions.find((s) => s.value === data.serviceL3)?.label ??
          data.serviceL3;

        formData.append('functionL1', functionL1Label);
        formData.append('typeL2', typeL2Label);
        formData.append('serviceL3', serviceL3Label);

        if (data.serviceL3) {
          formData.append('serviceId', data.serviceL3);
        }

        if (data.functionL1) {
          formData.append('functionL1Id', data.functionL1);
        }
        if (data.typeL2) {
          formData.append('typeL2Id', data.typeL2);
        }

        // Additionally send srAttemptId when in service details / Sama AI context
        if (storeSrAttemptId) {
          formData.append('srAttemptId', storeSrAttemptId);
        }
      }

      if (
        (data.topic === TopicCategory.ACTIVE_SERVICES ||
          data.topic === TopicCategory.REQUESTED_SERVICES) &&
        selectedSrAttemptId
      ) {
        // TDF-1342 (added 2026-09-09): "Requested Service"'s subject is
        // either a standalone service (srAttemptId) or a bundle
        // (bundleAttemptId) — exactly one, never both. requestedBundleIds
        // tracks which of the Requested-tab options selectedSrAttemptId
        // (a shared, generically-named piece of state) actually refers to.
        if (
          data.topic === TopicCategory.REQUESTED_SERVICES &&
          requestedBundleIds.has(selectedSrAttemptId)
        ) {
          formData.append('bundleAttemptId', selectedSrAttemptId);
        } else {
          formData.append('srAttemptId', selectedSrAttemptId);
        }
      }

      if (storeParentSrId) {
        formData.append('parentSrId', storeParentSrId);
      }

      // TDF-1388: Sub-Topic/Issue — mandatory together for Oracle ERP Issues (AC6),
      // absent for every other topic (validated in isFormValid, mirroring srAttemptId).
      if (
        data.topic === TopicCategory.ORACLE_ERP_ISSUES &&
        selectedSubTopic &&
        selectedIssueKey
      ) {
        formData.append('subTopic', selectedSubTopic);
        formData.append('issueKey', selectedIssueKey);
      }

      // TDF-1390: the selected item's own ServiceNow-driven fields, collected into a single
      // JSON-encoded 'answers' map keyed by variable name — matches the backend's
      // SupportTicketCreateRequestDTO.answers (and the BFF's CreateTicketRequestDto.answers,
      // which parses this same JSON string back into an object). Previously forwarded each
      // field under its own FormData key, which the BFF's whitelist-only ValidationPipe had
      // nowhere to put and would silently strip or 400 on.
      // File-type dynamic fields are already excluded from dynamicSections (TDF-1432,
      // excludeDuplicateFields) — they'd need their own upload wiring, not a plain multipart
      // field under the shared 'files' key.
      const answers: Record<string, string> = {};
      flattenFields(dynamicSections).forEach((dynamicField) => {
        const value = (data as Record<string, unknown>)[dynamicField.name];
        if (value !== undefined && value !== null && value !== '') {
          answers[dynamicField.name] = String(value);
        }
      });
      if (Object.keys(answers).length > 0) {
        formData.append('answers', JSON.stringify(answers));
      }

      uploadedFiles.forEach((uploadedFile: NewTicketUploadedFile) => {
        if (uploadedFile.file) {
          formData.append('files', uploadedFile.file);
        }
      });

      const response = await createTicket(formData);

      if (response) {
        trackEvent('support_ticket_initiated', { type: data.topic });
        triggerSuccess(response.ticketNumber, response);
        resetForm();
      } else {
        setShowError(true);
      }
    } catch (err) {
      const apiError = handleApiError(err) as ApiError;
      if (apiError?.code === 409) {
        setErrorMessage(
          resolveApiErrorMessage(
            apiError,
            t('supportTickets.drawer.ticketAlreadyExists')
          )
        );
      } else {
        setErrorMessage(undefined);
      }
      setShowError(true);
    }
  };

  const resetForm = () => {
    reset(newTicketFormDefaultValues);
    resetUploadState();
    setShowError(false);
    setErrorMessage(undefined);
    setFileError(undefined);
    setSelectedSrAttemptId(undefined);
    setSelectedSubTopic(undefined);
    setSelectedIssueKey(undefined);
    clearPrefilledTicketData();
  };

  const handleClose = () => {
    resetForm();
    close();
  };

  const handleFilesSelected = (files: FileList) => {
    setIsFileRemoved(false);
    const fileArray = Array.from(files);

    const newFiles: NewTicketUploadedFile[] = fileArray.map((file, index) => ({
      id: `file-${Date.now()}-${index}`,
      file,
      fileName: file.name,
      fileSize: `${Math.round(file.size / 1024)} KB`,
      bytes: file.size,
      progress: 0,
      status: 'pending',
    }));

    const validationResult = validateNewTicketUploadForm({
      uploadedFiles: newFiles.map((f) => ({
        id: f.id,
        fileName: f.fileName,
        fileSize: f.fileSize,
        bytes: f.bytes,
        progress: f.progress,
        status: f.status as any,
      })),
    });

    if (!validationResult.success) {
      const errorFiles = newFiles.map((f) => ({
        ...f,
        status: 'error' as const,
      }));
      addFiles(errorFiles);
      const result = validateFiles();
      setFileError(result.success ? undefined : result.error);
      return;
    }

    addFiles(newFiles);

    newFiles.forEach((file, index) => {
      startProgress({
        fileId: file.id,
        delayMs: index * 100,
        getLatestProgress: () =>
          uploadedFilesRef.current.find((f) => f.id === file.id)?.progress,
        onUpdate: (progress, status) => {
          updateFile(file.id, { progress, status });
          const result = validateFiles();
          setFileError(result.success ? undefined : result.error);
        },
      });
    });
  };

  const handleFileRemove = (fileId: string) => {
    setIsFileRemoved(true);
    cancelProgress(fileId);
    removeFile(fileId);
    const result = validateFiles();
    setFileError(result.success ? undefined : result.error);
  };

  if (!isMounted) {
    return null;
  }

  return (
    <SidePanel
      isOpen={visuallyOpen}
      onOpenChange={(open: boolean) => !open && handleClose()}
      position="left"
      width={'xs'}
      expandedWidth={'md'}
      title={t('supportTickets.drawer.title')}
      showCloseButton
      testID={testID}
      expandable
    >
      <FormProvider {...formMethods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col px-[16px] py-[24px] lg:p-3xl min-h-full"
        >
          {/* Scrollable Content Area */}
          <div className="flex-1">
            <div className="flex flex-col gap-4xl">
              {isServiceRequestContextMode ? (
                <InputWithLabel
                  id="service-l3"
                  label={t('supportTickets.drawer.serviceL3')}
                  value={effectiveServiceName ?? ''}
                  disabled
                  required
                  testID={`${testID}-service-l3`}
                />
              ) : (
                <>
                  {/* Topic Dropdown */}
                  <Controller
                    control={control}
                    name="topic"
                    render={({ field }) => (
                      <Dropdown
                        label={t('supportTickets.drawer.topic')}
                        id="topic"
                        options={[
                          {
                            value: TopicCategory.GENERAL,
                            label: t('supportTickets.drawer.general'),
                          },
                          ...(dropdownOptions && dropdownOptions.length > 0
                            ? [
                                {
                                  value: TopicCategory.SERVICE_SPECIFIC,
                                  label: t(
                                    'supportTickets.drawer.serviceSpecific'
                                  ),
                                },
                              ]
                            : []),
                          ...(activeServiceOptions.length > 0
                            ? [
                                {
                                  value: TopicCategory.ACTIVE_SERVICES,
                                  label: t(
                                    'supportTickets.drawer.activeServices'
                                  ),
                                },
                              ]
                            : []),
                          ...(requestedServiceOptions.length > 0
                            ? [
                                {
                                  value: TopicCategory.REQUESTED_SERVICES,
                                  label: t(
                                    'supportTickets.drawer.requestedServices'
                                  ),
                                },
                              ]
                            : []),
                          // TDF-1388 AC1/AC8: always shown, regardless of what services the
                          // client holds — unlike Service Specific/Active/Requested above.
                          {
                            value: TopicCategory.IT_SERVICES,
                            label: t('supportTickets.drawer.itServices'),
                          },
                          {
                            value: TopicCategory.ORACLE_ERP_ISSUES,
                            label: t('supportTickets.drawer.oracleErpIssues'),
                          },
                        ]}
                        value={field.value}
                        onChange={(selected: string) => {
                          const nextTopic = selected as TopicCategory;
                          // TDF-1432: setValue's shouldValidate (not field.onChange, which
                          // takes no options) is what reliably re-runs the resolver under
                          // mode: 'onChange' for a Controller-wrapped custom Dropdown.
                          setValue('topic', nextTopic, {
                            shouldValidate: true,
                            shouldTouch: true,
                          });
                          setSelectedSrAttemptId(undefined);
                          // TDF-1388 AC5: changing the topic clears the Sub-Topic and Issue too
                          setSelectedSubTopic(undefined);
                          setSelectedIssueKey(undefined);
                          if (nextTopic === TopicCategory.GENERAL) {
                            setValue('functionL1', '', {
                              shouldValidate: true,
                            });
                            setValue('typeL2', '', { shouldValidate: true });
                            setValue('serviceL3', '', { shouldValidate: true });
                          } else if (
                            nextTopic === TopicCategory.SERVICE_SPECIFIC
                          ) {
                            if (storeServiceId || storeServiceL3) {
                              populateServiceSpecificFields();
                            }
                          } else if (
                            nextTopic === TopicCategory.ACTIVE_SERVICES
                          ) {
                            setValue('functionL1', '', {
                              shouldValidate: true,
                            });
                            setValue('typeL2', '', { shouldValidate: true });
                            setValue('serviceL3', '', { shouldValidate: true });
                          } else if (
                            nextTopic === TopicCategory.REQUESTED_SERVICES
                          ) {
                            setValue('functionL1', '', {
                              shouldValidate: true,
                            });
                            setValue('typeL2', '', { shouldValidate: true });
                            setValue('serviceL3', '', { shouldValidate: true });
                          }
                        }}
                        className="w-full text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                        testID={`${testID}-topic-select`}
                        showLeftIcon={false}
                      />
                    )}
                  />

                  {/* Active Services — user picks from their active services list */}
                  {topic === TopicCategory.ACTIVE_SERVICES && (
                    <Dropdown
                      label={t('supportTickets.drawer.activeServiceLabel')}
                      id="active-service-select"
                      placeholder={t(
                        'supportTickets.drawer.activeServicePlaceholder'
                      )}
                      options={activeServiceOptions}
                      value={selectedSrAttemptId ?? ''}
                      onChange={(selected: string) =>
                        setSelectedSrAttemptId(selected || undefined)
                      }
                      onMenuScrollToBottom={() => {
                        if (
                          hasNextActiveServicesPage &&
                          !isFetchingNextActiveServicesPage
                        ) {
                          fetchNextActiveServicesPage();
                        }
                      }}
                      isFetchingMore={isFetchingNextActiveServicesPage}
                      className="w-full text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                      testID={`${testID}-active-service-select`}
                      showLeftIcon={false}
                    />
                  )}

                  {/* Requested Services — user picks from their requested services list */}
                  {topic === TopicCategory.REQUESTED_SERVICES && (
                    <Dropdown
                      label={t('supportTickets.drawer.requestedServiceLabel')}
                      id="requested-service-select"
                      placeholder={t(
                        'supportTickets.drawer.requestedServicePlaceholder'
                      )}
                      options={requestedServiceOptions}
                      value={selectedSrAttemptId ?? ''}
                      onChange={(selected: string) =>
                        setSelectedSrAttemptId(selected || undefined)
                      }
                      onMenuScrollToBottom={() => {
                        if (
                          hasNextRequestedServicesPage &&
                          !isFetchingNextRequestedServicesPage
                        ) {
                          fetchNextRequestedServicesPage();
                        }
                      }}
                      isFetchingMore={isFetchingNextRequestedServicesPage}
                      className="w-full text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                      testID={`${testID}-requested-service-select`}
                      showLeftIcon={false}
                    />
                  )}

                  {/* Service-specific fields */}
                  {topic === TopicCategory.SERVICE_SPECIFIC && (
                    <div
                      className="flex flex-col gap-4xl"
                      data-testid={`${testID}-service-specific-fields`}
                    >
                      {/* Function (L1) */}
                      <div className="flex flex-col">
                        <Controller
                          control={control}
                          name="functionL1"
                          render={({ field }) => (
                            <Dropdown
                              label={t('supportTickets.drawer.functionL1')}
                              id="function-l1"
                              placeholder={t(
                                'supportTickets.drawer.selectFunction'
                              )}
                              options={functionOptions.map((opt) => ({
                                value: opt.value,
                                label: opt.label,
                              }))}
                              value={field.value}
                              onChange={(selected: string) => {
                                setValue('functionL1', selected, {
                                  shouldValidate: true,
                                  shouldTouch: true,
                                });
                                setValue('typeL2', '', {
                                  shouldValidate: true,
                                });
                                setValue('serviceL3', '', {
                                  shouldValidate: true,
                                });
                              }}
                              className="w-full text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                              testID={`${testID}-function-l1`}
                              showLeftIcon={false}
                            />
                          )}
                        />
                      </div>

                      {/* Type (L2) */}
                      <div className="flex flex-col">
                        <Controller
                          control={control}
                          name="typeL2"
                          render={({ field }) => (
                            <Dropdown
                              label={t('supportTickets.drawer.typeL2')}
                              id="type-l2"
                              placeholder={t(
                                'supportTickets.drawer.selectType'
                              )}
                              options={typeOptions.map((opt) => ({
                                value: opt.value,
                                label: opt.label,
                              }))}
                              value={field.value}
                              onChange={(selected: string) => {
                                setValue('typeL2', selected, {
                                  shouldValidate: true,
                                  shouldTouch: true,
                                });
                                setValue('serviceL3', '', {
                                  shouldValidate: true,
                                });
                              }}
                              className="w-full text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                              testID={`${testID}-type-l2`}
                              showLeftIcon={false}
                            />
                          )}
                        />
                      </div>

                      {/* Service (L3) */}
                      <div className="flex flex-col">
                        <Controller
                          control={control}
                          name="serviceL3"
                          render={({ field }) => (
                            <Dropdown
                              label={t('supportTickets.drawer.serviceL3')}
                              id="service-l3"
                              placeholder={t(
                                'supportTickets.drawer.selectService'
                              )}
                              options={serviceOptions.map((opt) => ({
                                value: opt.value,
                                label: opt.label,
                              }))}
                              value={field.value}
                              onChange={(selected: string) =>
                                setValue('serviceL3', selected, {
                                  shouldValidate: true,
                                  shouldTouch: true,
                                })
                              }
                              className="w-full text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                              testID={`${testID}-service-l3`}
                              showLeftIcon={false}
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}

                  {/* Oracle ERP Issues — Sub-Topic → Issue cascade (TDF-1388) */}
                  {topic === TopicCategory.ORACLE_ERP_ISSUES && (
                    <div
                      className="flex flex-col gap-4xl"
                      data-testid={`${testID}-oracle-erp-fields`}
                    >
                      <Dropdown
                        label={t('supportTickets.drawer.subTopic')}
                        id="sub-topic"
                        placeholder={t('supportTickets.drawer.selectSubTopic')}
                        options={subTopicOptions}
                        value={selectedSubTopic ?? ''}
                        onChange={(selected: string) => {
                          setSelectedSubTopic(selected || undefined);
                          // TDF-1388 AC5: changing Sub-Topic clears the Issue selection —
                          // and since Subject was only ever prefilled from that issue's
                          // name (TDF-1390 AC3), clear it too rather than leave a subject
                          // on-screen with no selected issue behind it.
                          setSelectedIssueKey(undefined);
                          setValue('subject', '', { shouldValidate: true });
                        }}
                        className="w-full text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                        testID={`${testID}-sub-topic`}
                        showLeftIcon={false}
                      />

                      {selectedSubTopic && (
                        // TDF-1388 AC4: type-ahead — SearchInputSelection, same component
                        // Activity Requests already use for its filterable dropdown.
                        <SearchInputSelection
                          label={t('supportTickets.drawer.issue')}
                          placeholder={t('supportTickets.drawer.selectIssue')}
                          options={issueOptions}
                          value={selectedIssueKey ?? ''}
                          onChange={(selected: string) =>
                            setSelectedIssueKey(selected || undefined)
                          }
                          testID={`${testID}-issue`}
                        />
                      )}
                    </div>
                  )}

                  {/* TDF-1390: the selected item's own ServiceNow fields, once resolved.
                    Reuses the same DynamicFormRenderer/engine as Activity Requests. */}
                  {supportIssueProducerSysId &&
                    !schemaLoading &&
                    dynamicSections.length > 0 && (
                      <DynamicFormRenderer
                        sections={dynamicSections}
                        rules={dynamicRules}
                        testID={`${testID}-dynamic-fields`}
                      />
                    )}
                </>
              )}

              {/* Subject Field */}
              <Controller
                control={control}
                name="subject"
                render={({ field }) => (
                  <InputWithLabel
                    id="subject"
                    label={t('supportTickets.drawer.subject')}
                    placeholder={t('supportTickets.drawer.subjectPlaceholder')}
                    value={field.value}
                    onChange={field.onChange}
                    required
                    testID={`${testID}-subject`}
                  />
                )}
              />

              {/* Description Field */}
              <Controller
                control={control}
                name="description"
                render={({ field }) => (
                  <TextareaWithLabel
                    id="description"
                    placeholder={descriptionPlaceholder}
                    label={t('supportTickets.drawer.describeNeeds')}
                    value={field.value}
                    required={true}
                    onChange={field.onChange}
                    rows={6}
                    testID={`${testID}-description`}
                  />
                )}
              />

              {/* File Upload */}
              <DocumentUploadSection
                files={uploadedFiles}
                onFilesSelected={handleFilesSelected}
                onFileRemove={handleFileRemove}
                isFileRemoved={isFileRemoved}
                disableRemove={isCreatingTicket}
                errorMessage={fileError}
                accept=".pdf,.png,.jpg,.jpeg"
                multiple={true}
                supportingText={t('supportTickets.drawer.uploadSupportingText')}
                maxFiles={3}
                filesPosition="above"
                testID={testID ? `${testID}-upload` : undefined}
              />
            </div>
          </div>

          {/* Sticky Footer with Action Button */}
          <div className="mt-4xl">
            <div className={`flex ${showError ? '' : 'justify-end'}`}>
              {showError && (
                <div className="w-full flex justify-start text-error-600">
                  <Icon name="CircleAlert" className="me-2" />
                  <p
                    className="text-sm text-error-600"
                    data-testid={`${testID}-footer-error`}
                  >
                    {errorMessage ??
                      t('supportTickets.drawer.somethingWentWrong')}
                  </p>
                </div>
              )}
              <Button
                iconName="ArrowRight"
                type="submit"
                variant={'secondary'}
                className={
                  showError
                    ? 'w-50'
                    : 'inline-flex items-center justify-center gap-2'
                }
                testID={`${testID}-respond-button`}
                disabled={!isFormValid || isSubmitting}
                loading={isSubmitting}
                label={t('actions.submit').toUpperCase()}
              />
            </div>
          </div>
        </form>
      </FormProvider>
    </SidePanel>
  );
};
